// Admin-only: move every coach relationship a departed (inactive) concierge owns to an active staff member.
// Shown under a collapsed section on the Coaches page; the move is confirmed in-page with the count.
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useCrm } from '../../lib/crm';

const ERRORS = {
  same_user: 'Pick two different people.',
  not_active_staff: 'The new owner must be active staff.',
  from_still_active: 'Only a deactivated concierge\u2019s coaches can be reassigned.',
};

export default function ReassignPanel() {
  const { user } = useAuth();
  const crm = useCrm();
  const [open, setOpen] = useState(false);
  const [owners, setOwners] = useState([]);
  const [staff, setStaff] = useState([]); // active staff: colleagues plus the admin
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState('');

  const loadOwners = useCallback(() => crm.adminOwners().then(setOwners), [crm]);

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    Promise.all([crm.adminOwners(), crm.listColleagues()])
      .then(([o, c]) => {
        if (!live) return;
        setOwners(o);
        setStaff([{ id: user.id, name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Me' }, ...c.filter((x) => x.id !== user.id)]);
      })
      .catch(() => { if (live) setError('Could not load the owners.'); });
    return () => { live = false; };
  }, [open, crm, user]);

  if (user?.role !== 'admin') return null;

  // Only a departed concierge's list can be handed over; the server refuses an active owner (from_still_active).
  const departed = owners.filter((o) => !o.is_active);
  const fromOwner = departed.find((o) => String(o.user_id) === from);
  const toStaff = staff.find((s) => String(s.id) === to);
  const ready = fromOwner && fromOwner.relationship_count > 0 && toStaff && from !== to;

  const run = async () => {
    setBusy(true); setError('');
    try {
      const r = await crm.adminReassign(Number(from), Number(to));
      setResult(`Moved ${r.moved}, merged ${r.merged}`);
      setConfirming(false); setFrom(''); setTo('');
      await loadOwners().catch(() => {});
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(ERRORS[d] || 'Could not reassign those coaches.');
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="cr-reassign" data-testid="reassign_panel">
      <button type="button" className="adm-btn adm-btn--sm" aria-expanded={open} onClick={() => setOpen((o) => !o)} data-testid="reassign_toggle">
        Reassign a concierge&rsquo;s coaches
      </button>
      {open && (
        <div className="cr-form">
          <p className="cr-muted cr-form__wide" data-testid="reassign_hint">Deactivate a concierge&rsquo;s account to hand over their coaches.</p>
          <label className="adm-field">
            <span className="adm-field__label">From</span>
            <select className="adm-select" value={from} onChange={(e) => { setFrom(e.target.value); setConfirming(false); setResult(''); }} data-testid="reassign_from">
              <option value="">Choose a departed concierge</option>
              {departed.map((o) => (
                <option key={o.user_id} value={o.user_id}>{o.name} — {o.relationship_count}</option>
              ))}
            </select>
          </label>
          <label className="adm-field">
            <span className="adm-field__label">To</span>
            <select className="adm-select" value={to} onChange={(e) => { setTo(e.target.value); setConfirming(false); setResult(''); }} data-testid="reassign_to">
              <option value="">Choose active staff</option>
              {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          {confirming && ready ? (
            <div className="cr-form__wide cr-actions" data-testid="reassign_confirm">
              <strong>Move {fromOwner.relationship_count} {fromOwner.relationship_count === 1 ? 'coach' : 'coaches'} from {fromOwner.name} to {toStaff.name}?</strong>
              <button type="button" className="adm-btn adm-btn--primary" disabled={busy} onClick={run} data-testid="reassign_yes">Move</button>
              <button type="button" className="adm-btn" disabled={busy} onClick={() => setConfirming(false)}>Cancel</button>
            </div>
          ) : (
            <div className="cr-form__wide">
              <button type="button" className="adm-btn adm-btn--primary" disabled={!ready} onClick={() => setConfirming(true)} data-testid="reassign_go">Reassign</button>
            </div>
          )}
          {result && <div role="status" className="cr-muted cr-form__wide" data-testid="reassign_result">{result}</div>}
          {error && <div role="alert" className="cr-error cr-form__wide">{error}</div>}
        </div>
      )}
    </section>
  );
}
